/**
 * WhatIsASilo — the small clickable that answers the question the whole product
 * rests on, in Randy's own words.
 *
 * Oct 2026 (HOMEPAGE_PRICING_REVAMP.md §5) — it appears twice on the home page:
 * under the hero paragraph and under the pricing heading. One component, two
 * texts: Randy wrote a version for each position, so the second reading isn't
 * the first one again word for word. `variant` picks which.
 *
 * `dark` matches the pricing section's own theming switch.
 */
import { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";

const COPY = {
  // Top of the page — the first time anyone meets the idea.
  hero: [
    "A silo, in the data world, is a walled-off space where information stays protected from the outside world. My Silo protects not only the knowledge inside, but how AI interacts with it.",
    "Your knowledge doesn't train the large language model. Instead, the LLM comes to your Silo, uses your knowledge to answer the question, and leaves your knowledge where it belongs.",
  ],
  // Bottom of the page — the same truth, said to someone now weighing the price.
  pricing: [
    "A silo is a protected space, walled off from the outside world. My Silo takes that idea further by protecting both your knowledge and the way AI accesses it.",
    "Instead of handing your knowledge over to an LLM to learn from, My Silo brings the LLM to your knowledge. It finds what it needs, answers from it, and leaves your knowledge where it belongs.",
  ],
};

const CLOSER = "Your Silo doesn't teach the open world. It makes AI work for your world.";

export default function WhatIsASilo({ dark = false, variant = "hero", className = "", testid = "what-is-a-silo" }) {
  const [open, setOpen] = useState(false);
  const paras = COPY[variant] || COPY.hero;

  // Escape closes it — this is a reading panel, not a decision.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testid}
        className={`text-[11.5px] underline underline-offset-4 decoration-dotted transition-colors ${
          dark ? "text-white/55 hover:text-white/90" : "text-[#8A8884] hover:text-[#4A5D4E]"
        } ${className}`}
      >
        What is a Silo?
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-6 bg-[#1C1B1A]/55 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid={`${testid}-overlay`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testid}-panel`}
            className="relative w-full max-w-xl bg-[#FDFCF8] border hairline rounded-xl shadow-2xl px-6 py-7 sm:px-8 sm:py-8 max-h-[85vh] overflow-y-auto text-left"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={`${testid}-close`}
              aria-label="Close"
              className="absolute top-3 right-3 text-[#8A8884] hover:text-[#1C1B1A] transition-colors"
            >
              <X size={16} weight="bold" />
            </button>

            <h3 className="font-serif-display text-2xl sm:text-3xl text-[#1C1B1A] leading-tight">
              What is a Silo?
            </h3>
            <div className="mt-4 space-y-4 text-[13.5px] sm:text-sm text-[#3A3A38] leading-relaxed">
              {paras.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
              <p className="font-serif-display text-lg sm:text-xl text-[#1C1B1A] leading-snug">
                {CLOSER}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
